// src/components/NewWorkflow/AddNodeModal.jsx

import React, { useState, useEffect } from 'react';
import {
  X,
  Zap,
  Play,
  GitBranch,
  Database,
  Bot,
  LogIn,
  LogOut,
  Wand2,
  Calculator,
  UnfoldVertical,
  GitMerge,
  Brackets,
  ZapOff,
  Network,
} from 'lucide-react';

const nodeCategories = [
  // ... (your existing nodeCategories array remains the same)
  {
    name: 'Basic',
    nodes: [
      {
        type: 'trigger',
        label: 'Trigger',
        description: 'Starts the workflow based on an event.',
        icon: <Zap className="h-6 w-6 text-yellow-400" />,
      },
      {
        type: 'action',
        label: 'Action',
        description: 'Performs a specific task in an application.',
        icon: <Play className="h-6 w-6 text-blue-400" />,
      },
      {
        type: 'conditional',
        label: 'Conditional',
        description: 'Branches logic based on if/else conditions.',
        icon: <GitBranch className="h-6 w-6 text-purple-400" />,
      },
      {
        type: 'data',
        label: 'Data',
        description: 'Accesses data from your integrated sources.',
        icon: <Database className="h-6 w-6 text-green-400" />,
      },
      {
        type: 'agent',
        label: 'Agent',
        description: 'Delegates tasks to a pre-configured AI agent.',
        icon: <Bot className="h-6 w-6 text-pink-400" />,
      },
    ],
  },
  {
    name: 'Tool',
    nodes: [
      {
        type: 'toolInput',
        label: 'Tool Input',
        description: 'Defines the inputs for a custom agent tool.',
        icon: <LogIn className="h-6 w-6 text-emerald-400" />,
      },
      {
        type: 'toolOutput',
        label: 'Tool Output',
        description: 'Defines the outputs from a custom agent tool.',
        icon: <LogOut className="h-6 w-6 text-orange-400" />,
      },
      {
        type: 'structuralConditional',
        label: 'Structural Condition',
        description: 'Branches logic based on data structure.',
        icon: <Network className="h-6 w-6 text-teal-400" />,
      },
    ],
  },
  {
    name: 'Structure',
    nodes: [
      {
        type: 'customAgent',
        label: 'Custom Agent',
        description: 'Build a powerful agent on structured data.',
        icon: <Wand2 className="h-6 w-6 text-cyan-400" />,
      },
      {
        type: 'operations',
        label: 'Operations',
        description: 'Perform calculations or data transformations.',
        icon: <Calculator className="h-6 w-6 text-amber-400" />,
      },
      {
        type: 'unwrapArray',
        label: 'Unwrap Array',
        description: 'Process each item in an array individually.',
        icon: <UnfoldVertical className="h-6 w-6 text-teal-400" />,
      },
      {
        type: 'restructure',
        label: 'Restructure',
        description: 'Remap and transform data fields and structure.',
        icon: <GitMerge className="h-6 w-6 text-indigo-400" />,
      },
      // { type: 'dataSplitter', label: 'Data Splitter', description: 'Split data into multiple paths.', icon: <Split className="w-6 h-6 text-lime-400" /> },
      {
        type: 'dataArray',
        label: 'Data Array',
        description: 'Create and manage arrays of data.',
        icon: <Brackets className="h-6 w-6 text-fuchsia-400" />,
      },
      {
        type: 'loopBreak',
        label: 'Loop Break',
        description: 'Stops a loop by merging parallel branches.',
        icon: <ZapOff className="h-6 w-6 text-rose-400" />,
      },
    ],
  },
];

const AddNodeModal = ({ isOpen, onClose, onSelectNode }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCategories, setFilteredCategories] = useState(nodeCategories);

  // Effect for keyboard input
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        setSearchQuery(''); // Clear the entire query at once
      } else if (e.key.length === 1 && /[a-zA-Z0-9\s]/.test(e.key)) {
        e.preventDefault();
        setSearchQuery((prev) => prev + e.key);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Effect for filtering based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCategories(nodeCategories);
      return;
    }
    const lowercasedQuery = searchQuery.toLowerCase();
    const filtered = nodeCategories
      .map((category) => ({
        ...category,
        nodes: category.nodes.filter(
          (node) =>
            node.label.toLowerCase().includes(lowercasedQuery) ||
            node.description.toLowerCase().includes(lowercasedQuery),
        ),
      }))
      .filter((category) => category.nodes.length > 0);
    setFilteredCategories(filtered);
  }, [searchQuery]);

  // Effect for auto-clearing the search after a timeout
  useEffect(() => {
    if (!searchQuery) return;

    const timeoutId = setTimeout(() => {
      setSearchQuery('');
    }, 20000); // 20-second timeout

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  if (!isOpen) return null;

  const handleNodeSelect = (nodeType) => {
    onSelectNode(nodeType);
    onClose();
  };

  return (
    // eslint-disable-next-line
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-70 p-4"
      onClick={onClose}
    >
      {/* eslint-disable-next-line */}
      <div
        className="flex max-h-[80vh] w-full max-w-3xl flex-col rounded-lg border border-zinc-700 bg-zinc-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center gap-4 border-b border-zinc-800 p-4">
          <h2 className="shrink-0 text-xl font-semibold text-zinc-100">
            Add a New Step
          </h2>

          {/* Search query is now displayed here */}
          <div className="flex-1 truncate text-right text-sm text-zinc-500">
            {searchQuery && (
              <span className="flex items-center justify-end">
                <span className="font-medium text-zinc-300">{searchQuery}</span>
                <span
                  className="ml-2 h-4 w-[2px] animate-pulse bg-[#5652CF]"
                  style={{ animation: 'blink 1s step-end infinite' }}
                ></span>
              </span>
            )}
          </div>

          <button
            onClick={onClose}
            className="rounded-full p-1 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-100"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <main className="overflow-y-auto p-6">
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category) => (
              <div key={category.name} className="mb-8">
                <h3 className="mb-4 px-1 text-lg font-medium text-zinc-300">
                  {category.name}
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {category.nodes.map((node) => (
                    <button
                      key={node.type}
                      onClick={() => handleNodeSelect(node.type)}
                      className="flex transform flex-col items-start space-y-2 rounded-lg border border-zinc-700 bg-zinc-800/50 p-4 text-left transition-all hover:-translate-y-1 hover:border-[#5652CF] hover:bg-zinc-800"
                    >
                      <div className="flex items-center gap-3">
                        {node.icon}
                        <span className="font-semibold text-zinc-100">
                          {node.label}
                        </span>
                      </div>
                      <p className="text-sm leading-snug text-zinc-400">
                        {node.description}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="py-10 text-center text-zinc-500">
              <p className="text-lg font-semibold">
                No results found for "{searchQuery}"
              </p>
              <p className="mt-1 text-sm">
                Press Backspace or Delete to clear search.
              </p>
            </div>
          )}
        </main>
      </div>
      {/* Keyframes for the cursor blink animation */}
      <style>
        {`@keyframes blink { from, to { background-color: transparent } 50% { background-color: #3b82f6; } }`}
      </style>
    </div>
  );
};

export default AddNodeModal;
